/* Common Imports */

import { styled, Theme, useMediaQuery, useTheme } from "@mui/system";
import React from "react";
import { WhatsApp } from "@mui/icons-material";

/* Redux & Type Imports */

import { NextRouter } from "next/router";
import StateInterface from "../../../../redux-magic/state-interface";
import {
	deleteChatThunk,
	muteChatThunk,
	setChatScreenThunk,
	setCurrentChatSettingsThunk,
	setOpenChatModalThunk,
} from "../../../../redux-magic/store";
import { Participants } from "../../../../redux-magic/sub-interfaces/chat-interface";
import { useStore } from "react-redux";

/* Component Imports */

import {
	Alert,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	MenuItem,
	Popover,
	Snackbar,
	SvgIcon,
	Tooltip,
	Typography,
} from "@mui/material";

/* Icon Imports */

import { ArrowBackOutlined, ExpandMoreOutlined, LocalPhoneOutlined, MoreVertOutlined } from "@mui/icons-material";
import CallIcon from "@mui/icons-material/Call";
import PremiumBadge from "/public/images/badges/verified_premium.svg";

/* Funtion Imports */

import BusinessProfileTypeString from "../../../../lib/BusinessProfileTypeString";
import { formatLastSeen } from "../../../../lib/chatDates";
import ChatReportDialog from "./ChatReportDialog";
import ChatSharedDialog from "./ChatSharedDialog";
import BeegruButton from "../../../common-components/buttons/BeegruButton";

/* Styled Components */

const RootDiv = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	borderBottom: "#E5E5E5 1px solid",
	padding: "1rem 0.25rem 1rem 0.25rem",
	[theme.breakpoints.up("md1190")]: {
		padding: "1.5rem 1.25rem 1rem 1.25rem",
	},
}));

const ColumnContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "flex-start",
	height: "100%",
	padding: "0rem",
	gap: "0.25rem",
	overflow: "hidden",
}));

const RowContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
}));

const CustomImg = styled("img")(({ theme }) => ({
	borderRadius: "0.5rem",
	boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
	height: "3rem",
	width: "3rem",
	objectFit: "cover",
	[theme.breakpoints.up("md1190")]: {
		height: "3rem",
		width: "3rem",
	},
}));

const Heading = styled(Typography)(({ theme }) => ({
	fontSize: "0.825rem",
	fontWeight: 500,
	// cursor: "pointer",
	color: "#6e3037",
	whiteSpace: "nowrap",
	overflow: "hidden",
	textOverflow: "ellipsis",
	maxWidth: "5rem",
	display: "block",
	/* galaxy S8+ - 360 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 78)]: {
		maxWidth: "7.5rem",
	},
	/* galaxy S8+ - 385 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 103)]: {
		maxWidth: "9rem",
	},
	/*iphone XR - 414*/
	[theme.breakpoints.up(theme.breakpoints.values.xs + 130)]: {
		maxWidth: "10rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1rem",
	},
}));

const SubHeading = styled(Typography)(({ theme }) => ({
	fontSize: "0.825rem",
	fontWeight: 400,
	color: theme.palette.text.secondary,
	[theme.breakpoints.up("md1190")]: {
		fontSize: "0.875rem",
	},
}));

const CustomMenuItem = styled(MenuItem)(({ theme }) => ({
	fontSize: "0.825rem",
	padding: "0rem 0.75rem 0rem 0.75rem",
	borderRadius: "0.75rem",
	width: "100%",
	[theme.breakpoints.up("md1190")]: {
		fontSize: "0.875rem",
		padding: "1rem 1rem 1rem 1rem",
	},
}));

const ChatHeader = ({
	session,
	dispatch,
	router,
	profile_context,
	chat_id,
	chat_type,
	chat_title,
	chat_description,
	chat_is_broadcast,
	private_chat_receiver,
	desktop_fullscreen_mode,
	chat_source_collection,
	chat_is_muted,
	my_block_id,
	is_typing,
	chat_broadcast_id,
	on_open_search,
}: {
	session: StateInterface["redux_session"];
	dispatch: Function;
	router: NextRouter;
	profile_context: any;
	chat_id: string;
	chat_type: string;
	chat_title: string;
	chat_description: string;
	chat_is_broadcast: boolean;
	private_chat_receiver: Participants;
	desktop_fullscreen_mode: boolean;
	chat_source_collection: string | undefined;
	/* The signed-in person muted this chat, and the block this side made against the other ("" when
	   none), from chat/fetch-messages (lib/chat/chatSettings.ts, 18 Oct 2026). */
	chat_is_muted?: boolean;
	my_block_id?: string;
	/* The other side is typing (18 Oct 2026): "typing…" in place of their profile type. */
	is_typing?: boolean;
	/* The newest broadcast a chat-less broadcast row stands for (report it by its broadcast, 18 Oct 2026). */
	chat_broadcast_id?: string;
	/* "Search" in the menu (gap A9): the screen swaps this header for the search field. */
	on_open_search?: () => void;
}) => {
	const theme = useTheme();

	// Lets check if the device is a mobile device or not

	const smDevice = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

	/* Translations */

	const localLang = {
		verified: router.locale === "ar-AE" ? "تم التحقق" : router.locale === "kn-IN" ? "ಪರಿಶೀಲಿಸಲಾಗಿದೆ" : "Verified",
		premium_user:
			router.locale === "ar-AE" ? "مستخدم مميز" : router.locale === "kn-IN" ? "ಪ್ರೀಮಿಯಂ ಬಳಕೆದಾರ" : "Premium user",
		client: router.locale === "ar-AE" ? "عميل" : router.locale === "kn-IN" ? "ಗ್ರಾಹಕ" : "Client",
		media_links_docs:
			router.locale === "ar-AE" ? "الوسائط والروابط والمستندات" : router.locale === "kn-IN" ? "ಮೀಡಿಯಾ, ಲಿಂಕ್‌ಗಳು ಮತ್ತು ದಾಖಲೆಗಳು" : "Media, links and docs",
		search: router.locale === "ar-AE" ? "بحث" : router.locale === "kn-IN" ? "ಹುಡುಕಿ" : "Search",
		delete_chat: router.locale === "ar-AE" ? "حذف الدردشة" : router.locale === "kn-IN" ? "ಚಾಟ್ ಅಳಿಸಿ" : "Delete chat",
		block: router.locale === "ar-AE" ? "حظر" : router.locale === "kn-IN" ? "ನಿರ್ಬಂಧಿಸಿ" : "Block",
		unblock: router.locale === "ar-AE" ? "إلغاء الحظر" : router.locale === "kn-IN" ? "ನಿರ್ಬಂಧ ತೆಗೆಯಿರಿ" : "Unblock",
		unblock_title:
			router.locale === "ar-AE"
				? "إلغاء حظر هذا الحساب؟"
				: router.locale === "kn-IN"
					? "ಈ ಖಾತೆಯ ನಿರ್ಬಂಧ ತೆಗೆಯಬೇಕೇ?"
					: "Unblock this profile?",
		unblock_body:
			router.locale === "ar-AE"
				? "ستتمكنان من مراسلة بعضكما مرة أخرى في هذه المحادثة."
				: router.locale === "kn-IN"
					? "ಈ ಚಾಟ್‌ನಲ್ಲಿ ನೀವಿಬ್ಬರೂ ಮತ್ತೆ ಸಂದೇಶ ಕಳುಹಿಸಬಹುದು."
					: "You'll both be able to message each other again in this chat.",
		unblock_error:
			router.locale === "ar-AE"
				? "تعذر إلغاء حظر هذا الحساب. يرجى المحاولة مرة أخرى"
				: router.locale === "kn-IN"
					? "ಈ ಖಾತೆಯ ನಿರ್ಬಂಧ ತೆಗೆಯಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ"
					: "Couldn't unblock this account. Please try again",
		mute: router.locale === "ar-AE" ? "كتم" : router.locale === "kn-IN" ? "ಮ್ಯೂಟ್ ಮಾಡಿ" : "Mute",
		unmute: router.locale === "ar-AE" ? "إلغاء الكتم" : router.locale === "kn-IN" ? "ಅನ್‌ಮ್ಯೂಟ್ ಮಾಡಿ" : "Unmute",
		mute_title:
			router.locale === "ar-AE" ? "كتم هذه المحادثة؟" : router.locale === "kn-IN" ? "ಈ ಚಾಟ್ ಮ್ಯೂಟ್ ಮಾಡಬೇಕೇ?" : "Mute this chat?",
		unmute_title:
			router.locale === "ar-AE"
				? "إلغاء كتم هذه المحادثة؟"
				: router.locale === "kn-IN"
					? "ಈ ಚಾಟ್ ಅನ್‌ಮ್ಯೂಟ್ ಮಾಡಬೇಕೇ?"
					: "Unmute this chat?",
		mute_body:
			router.locale === "ar-AE"
				? "لن تصلك إشعارات الرسائل الجديدة هنا. سيظل الآخرون في فريقك يتلقونها."
				: router.locale === "kn-IN"
					? "ಇಲ್ಲಿನ ಹೊಸ ಸಂದೇಶಗಳ ಅಧಿಸೂಚನೆಗಳು ನಿಮಗೆ ಬರುವುದಿಲ್ಲ. ನಿಮ್ಮ ತಂಡದ ಇತರರಿಗೆ ಬರುತ್ತವೆ."
					: "You won't get notifications for new messages here. Others on your team still will.",
		unmute_body:
			router.locale === "ar-AE"
				? "ستصلك إشعارات الرسائل الجديدة هنا مرة أخرى."
				: router.locale === "kn-IN"
					? "ಇಲ್ಲಿನ ಹೊಸ ಸಂದೇಶಗಳ ಅಧಿಸೂಚನೆಗಳು ನಿಮಗೆ ಮತ್ತೆ ಬರುತ್ತವೆ."
					: "You'll get notifications for new messages here again.",
		mute_error:
			router.locale === "ar-AE"
				? "تعذر حفظ ذلك. يرجى المحاولة مرة أخرى"
				: router.locale === "kn-IN"
					? "ಅದನ್ನು ಉಳಿಸಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ"
					: "Couldn't save that. Please try again",
		report:
			router.locale === "ar-AE" ? "إبلاغ" : router.locale === "kn-IN" ? "ವರದಿ ಮಾಡಿ" : "Report",
		also_report:
			router.locale === "ar-AE"
				? "الإبلاغ عن هذا الملف الشخصي إلى بيغرو أيضًا"
				: router.locale === "kn-IN"
					? "ಈ ಪ್ರೊಫೈಲ್ ಅನ್ನು Beegru ಗೆ ವರದಿ ಮಾಡಿ ಕೂಡ"
					: "Also report this profile to Beegru",
		report_sent:
			router.locale === "ar-AE"
				? "شكرًا على الإبلاغ. سيراجعه فريقنا."
				: router.locale === "kn-IN"
					? "ವರದಿ ಮಾಡಿದ್ದಕ್ಕೆ ಧನ್ಯವಾದಗಳು. ನಮ್ಮ ತಂಡ ಪರಿಶೀಲಿಸುತ್ತದೆ."
					: "Thanks for reporting. Our team will look into it",
		report_and_block_sent:
			router.locale === "ar-AE"
				? "تم الإبلاغ والحظر. لم يعد بإمكانهم مراسلتك."
				: router.locale === "kn-IN"
					? "ವರದಿ ಮಾಡಲಾಗಿದೆ ಮತ್ತು ನಿರ್ಬಂಧಿಸಲಾಗಿದೆ. ಅವರು ಇನ್ನು ನಿಮಗೆ ಸಂದೇಶ ಕಳುಹಿಸಲು ಸಾಧ್ಯವಿಲ್ಲ."
					: "Reported and blocked. They can no longer message you",
		block_title:
			router.locale === "ar-AE"
				? "لماذا تحظر هذا الحساب؟"
				: router.locale === "kn-IN"
					? "ನೀವು ಈ ಖಾತೆಯನ್ನು ಏಕೆ ನಿರ್ಬಂಧಿಸುತ್ತಿದ್ದೀರಿ?"
					: "Why are you blocking this account?",
		block_body:
			router.locale === "ar-AE"
				? "لن يتمكنوا من مراسلتك، وتبقى المحادثة للقراءة فقط. يمكنك إلغاء الحظر من إعدادات الخصوصية."
				: router.locale === "kn-IN"
					? "ಅವರು ನಿಮಗೆ ಸಂದೇಶ ಕಳುಹಿಸಲು ಸಾಧ್ಯವಿಲ್ಲ, ಮತ್ತು ಈ ಚಾಟ್ ಓದಲು ಮಾತ್ರ ಉಳಿಯುತ್ತದೆ. ಗೌಪ್ಯತೆ ಸೆಟ್ಟಿಂಗ್‌ಗಳಿಂದ ನಿರ್ಬಂಧ ತೆಗೆದುಹಾಕಬಹುದು."
					: "They won't be able to message you, and this chat stays read-only. You can unblock them in Privacy settings.",
		block_error:
			router.locale === "ar-AE"
				? "تعذر حظر هذا الحساب. يرجى المحاولة مرة أخرى"
				: router.locale === "kn-IN"
					? "ಈ ಖಾತೆಯನ್ನು ನಿರ್ಬಂಧಿಸಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ"
					: "Couldn't block this account. Please try again",
		back_to_chats:
			router.locale === "ar-AE"
				? "العودة إلى الدردشات"
				: router.locale === "kn-IN"
					? "ಚಾಟ್‌ಗಳಿಗೆ ಹಿಂತಿರುಗಿ"
					: "Back to chats",
		options: router.locale === "ar-AE" ? "خيارات" : router.locale === "kn-IN" ? "ಆಯ್ಕೆಗಳು" : "Options",
		call: router.locale === "ar-AE" ? "اتصال" : router.locale === "kn-IN" ? "ಕರೆ ಮಾಡಿ" : "Call",
		close: router.locale === "ar-AE" ? "إغلاق" : router.locale === "kn-IN" ? "ಮುಚ್ಚಿ" : "Close",
		yes_delete: router.locale === "ar-AE" ? "نعم، احذف" : router.locale === "kn-IN" ? "ಹೌದು, ಅಳಿಸಿ" : "Yes, delete",
		no_cancel: router.locale === "ar-AE" ? "لا، إلغاء" : router.locale === "kn-IN" ? "ಇಲ್ಲ, ರದ್ದುಮಾಡಿ" : "No, cancel",
		delete_chat_confirmation:
			router.locale === "ar-AE"
				? "هل أنت متأكد أنك تريد حذف هذه المحادثة؟"
				: router.locale === "kn-IN"
					? "ಈ ಚಾಟ್ ಅನ್ನು ಅಳಿಸಲು ನೀವು ಖಚಿತವಾಗಿ ಬಯಸುವಿರಾ?"
					: "Are you sure you want to delete this chat?",
		delete_chat_error:
			router.locale === "ar-AE"
				? "تعذر حذف الدردشة. يرجى المحاولة مرة أخرى"
				: router.locale === "kn-IN"
					? "ಚಾಟ್ ಅಳಿಸಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ"
					: "Couldn't delete the chat. Please try again",
		// Same copy as the property page's contact card (ContactDetailCard).
		view_number:
			router.locale === "ar-AE" ? "عرض الرقم" : router.locale === "kn-IN" ? "ಫೋನ್ ಸಂಖ್ಯೆಯನ್ನು ವೀಕ್ಷಿಸಿ" : "View Number",
		mention_snack:
			router.locale === "ar-AE"
				? "لا تنس أن تذكر Beegru.com عندما تتصل"
				: router.locale === "kn-IN"
					? "ನೀವು ಕಾಲ್ ಮಾಡಿದಾಗ Beegru.com ಅನ್ನು ಹೇಳಲು ಮರೆತಿರಬೇಡಿ"
					: "Mention Beegru.com during your call for smoother communication.",
	};

	/* Options Menu */

	const [anchorEl, setAnchorEl] = React.useState<Element | null>(null);

	const handleOptionsMenuClick = (event: React.MouseEvent<Element>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleOptionsMenuClose = () => {
		setAnchorEl(null);
	};

	const open = Boolean(anchorEl);
	const id = open ? "simple-popover" : undefined;

	/* Delete Dialog */

	const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);

	/* Delete progress (chat audit I11, 17 Oct 2026). The dialog stays open while the request runs,
	   the confirm button shows its loader, and a failure keeps the dialog open with a short error
	   line. The thunk returns nothing, so success is read from the store: the deleteChat reducer
	   clears the open chat only when the server said yes. */

	const store = useStore<{ globalState: StateInterface }>();

	const [deletingChat, setDeletingChat] = React.useState<boolean>(false);

	const [deleteChatError, setDeleteChatError] = React.useState<boolean>(false);

	const mountedRef = React.useRef<boolean>(true);

	React.useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	const handleOpenDeleteDialog = () => {
		setDeleteChatError(false);
		setOpenDeleteDialog(true);
		handleOptionsMenuClose(); // Close options menu
	};

	const handleCloseDeleteDialog = () => {
		// Not while the delete is running: the dialog closes on its result.
		if (deletingChat) return;
		setOpenDeleteDialog(false);
		setDeleteChatError(false);
	};

	const handleDeleteChat = async () => {
		if (deletingChat) return;
		setDeletingChat(true);
		setDeleteChatError(false);
		let deleted: boolean = false;
		try {
			await dispatch(
				deleteChatThunk({
					chat_id: chat_id,
					is_business_profile: profile_context.is_business_profile,
					user_id: session ? (session.user ? session.user.dbUser._id : "") : "",
					business_profile_id: profile_context.business_profile_id,
					sessionId: session ? (session.session_id ? session.session_id : "") : "",
				}),
			);
			const open_chat_id = store.getState().globalState?.chat_states?.current_chat?.chat_id;
			deleted = String(open_chat_id || "") !== String(chat_id || "");
		} catch {
			deleted = false;
		}
		if (!mountedRef.current) return;
		setDeletingChat(false);
		if (deleted) {
			setOpenDeleteDialog(false);
		} else {
			setDeleteChatError(true);
		}
	};

	/* Block (chat audit Q12, 17 Oct 2026), the app's flow: pick at least one reason, then Block. The
	   block is made as the acting business (owner, admin or a member holding Chat) or, acting
	   personally, as the person (personal blocks are made on the website only). The server decides
	   (account-settings/blocked-accounts/block-user, lib/auth/blockAccounts.ts); on success the
	   thread freezes, and send-message refuses from then on. */

	// The app's reasons (ConversationViewModel BLOCK_REASONS), stored as written.
	const BLOCK_REASONS: Array<string> = [
		"Harassment or abusive behavior",
		"Spam or promotional messages",
		"Not relevant to my requirement",
		"Inappropriate content",
		"Other",
	];

	const [openBlockDialog, setOpenBlockDialog] = React.useState<boolean>(false);
	const [blockReasons, setBlockReasons] = React.useState<Array<string>>([]);
	/* "Also report this profile" (18 Oct 2026): the block's reasons go to Beegru as a report too. */
	const [blockAlsoReport, setBlockAlsoReport] = React.useState<boolean>(false);
	/* Reporting the profile (18 Oct 2026, ChatReportDialog), and the line said once it is done. */
	const [openReportDialog, setOpenReportDialog] = React.useState<boolean>(false);
	/* What has been shared in this chat (gap A10, the app's chat profile cards). */
	const [openSharedDialog, setOpenSharedDialog] = React.useState<boolean>(false);
	const [reportNotice, setReportNotice] = React.useState<string>("");
	const [blocking, setBlocking] = React.useState<boolean>(false);
	const [blockError, setBlockError] = React.useState<boolean>(false);

	const handleOpenBlockDialog = () => {
		setBlockReasons([]);
		setBlockAlsoReport(false);
		setBlockError(false);
		setOpenBlockDialog(true);
		handleOptionsMenuClose();
	};

	const handleCloseBlockDialog = () => {
		if (blocking) return;
		setOpenBlockDialog(false);
	};

	const idOf = (value: any): string =>
		value && typeof value === "object" && "_id" in value ? String(value._id || "") : value ? String(value) : "";

	const handleBlock = async () => {
		if (blocking || blockReasons.length === 0) return;
		const receiverIsBusiness: boolean = private_chat_receiver?.is_business_profile === true;
		const targetId: string = receiverIsBusiness
			? idOf(private_chat_receiver?.business_profile_id)
			: idOf(private_chat_receiver?.user_id);
		if (!targetId) {
			setBlockError(true);
			return;
		}
		setBlocking(true);
		setBlockError(false);
		let blocked: boolean = false;
		let blockId: string = "";
		try {
			const response = await fetch(process.env.PRODUCTION_API_URL + "account-settings/blocked-accounts/block-user", {
				method: "POST",
				cache: "no-store",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					...(receiverIsBusiness ? { blocked_business_profile_id: targetId } : { blocked_user_id: targetId }),
					...(profile_context?.is_business_profile === true && profile_context?.business_profile_id
						? { business_profile_id: String(profile_context.business_profile_id) }
						: {}),
					reasons: blockReasons,
				}),
			});
			const result = await response.json().catch(() => null);
			blocked = response.ok && result !== null && result.status === "success";
			blockId = blocked && typeof result?.payload?.block_id === "string" ? result.payload.block_id : "";
		} catch {
			blocked = false;
		}
		if (!mountedRef.current) return;
		setBlocking(false);
		if (blocked) {
			setOpenBlockDialog(false);
			// The thread freezes, and the menu now offers Unblock.
			dispatch(setCurrentChatSettingsThunk({ is_blocked: true, my_block_id: blockId }));
			// "Also report": the same reasons, as a report of the profile. Never undoes the block.
			if (blockAlsoReport) {
				fetch(process.env.PRODUCTION_API_URL + "chat/report", {
					method: "POST",
					cache: "no-store",
					body: JSON.stringify({
						...(chat_source_collection === "broadcast" ? { broadcast_id: String(chat_broadcast_id || "") } : { chat_id }),
						reasons: blockReasons,
						is_business_profile: profile_context?.is_business_profile === true,
						user_id: profile_context?.user_id,
						business_profile_id: profile_context?.business_profile_id,
					}),
				})
					.then((res) => res.json())
					.then((data) => {
						if (mountedRef.current && data?.status === "success") setReportNotice(localLang.report_and_block_sent);
					})
					.catch(() => {});
			}
		} else {
			setBlockError(true);
		}
	};

	/* Unblock (18 Oct 2026): this side's own block, lifted after a confirm. The thread opens again
	   unless the other side blocked this one too, which the next send or load says. */

	const blockedByMe: boolean = !!my_block_id;
	const [openUnblockDialog, setOpenUnblockDialog] = React.useState<boolean>(false);
	const [unblocking, setUnblocking] = React.useState<boolean>(false);
	const [unblockError, setUnblockError] = React.useState<boolean>(false);

	const handleBlockMenuItem = () => {
		if (!blockedByMe) {
			handleOpenBlockDialog();
			return;
		}
		setUnblockError(false);
		setOpenUnblockDialog(true);
		handleOptionsMenuClose();
	};

	const handleUnblock = async () => {
		if (unblocking || !my_block_id) return;
		setUnblocking(true);
		setUnblockError(false);
		let done: boolean = false;
		try {
			const response = await fetch(process.env.PRODUCTION_API_URL + "account-settings/blocked-accounts/unblock-user", {
				method: "POST",
				cache: "no-store",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ block_id: my_block_id }),
			});
			const result = await response.json().catch(() => null);
			done = response.ok && result !== null && result.status === "success";
		} catch {
			done = false;
		}
		if (!mountedRef.current) return;
		setUnblocking(false);
		if (done) {
			setOpenUnblockDialog(false);
			dispatch(setCurrentChatSettingsThunk({ is_blocked: false, my_block_id: "" }));
		} else {
			setUnblockError(true);
		}
	};

	/* Mute (18 Oct 2026): for the signed-in person only (lib/chat/chatSettings.ts), after a confirm,
	   the app's menu item. The dialog keeps the words it opened with while it closes. */

	const [openMuteDialog, setOpenMuteDialog] = React.useState<boolean>(false);
	const [muteDialogMutes, setMuteDialogMutes] = React.useState<boolean>(true);
	const [savingMute, setSavingMute] = React.useState<boolean>(false);
	const [muteError, setMuteError] = React.useState<boolean>(false);

	const handleOpenMuteDialog = () => {
		setMuteDialogMutes(chat_is_muted !== true);
		setMuteError(false);
		setOpenMuteDialog(true);
		handleOptionsMenuClose();
	};

	const handleCloseMuteDialog = () => {
		if (savingMute) return;
		setOpenMuteDialog(false);
	};

	const handleMute = async () => {
		if (savingMute) return;
		setSavingMute(true);
		setMuteError(false);
		const ok: boolean = await dispatch(
			muteChatThunk({
				chat_id: chat_id,
				muted: muteDialogMutes,
				is_business_profile: profile_context?.is_business_profile === true,
				business_profile_id: String(profile_context?.business_profile_id || ""),
			}),
		);
		if (!mountedRef.current) return;
		setSavingMute(false);
		if (ok) {
			setOpenMuteDialog(false);
		} else {
			setMuteError(true);
		}
	};

	/* Premium Tooltip */

	const [openPremiumTooltip, setOpenPremiumTooltip] = React.useState(false);

	/* Chat Information */

	const is_user_business_profile: boolean =
		private_chat_receiver.is_business_profile === true &&
		typeof private_chat_receiver.business_profile_id === "object" &&
		private_chat_receiver.business_profile_id !== null;

	/* Call button contact (16 Oct 2026). Chat payloads carry no phone numbers, so the number
	   comes from chat/fetch-profile-details, which applies the one contact rule (the business
	   chose `show_contact_details`, no block either way; lib/chat/contactVisibility). It is
	   asked for only for a business receiver, and no number back means no button.
	   Chat audit D8, 17 Oct 2026: it is now asked for on every width, because desktop shows the
	   property page's copy-number button where a small screen shows the call button. */

	const receiver_business_profile_id: string =
		is_user_business_profile &&
		typeof private_chat_receiver.business_profile_id === "object" &&
		private_chat_receiver.business_profile_id !== null &&
		"_id" in private_chat_receiver.business_profile_id
			? String(private_chat_receiver.business_profile_id._id || "")
			: "";

	const viewer_is_business_profile: boolean = profile_context?.is_business_profile === true;

	const viewer_business_profile_id: string = viewer_is_business_profile
		? String(profile_context?.business_profile_id || "")
		: "";

	const [chat_user_phone_with_country_code, setChatUserPhoneWithCountryCode] = React.useState<string>("");

	React.useEffect(() => {
		setChatUserPhoneWithCountryCode("");
		if (receiver_business_profile_id.length === 0) return;
		let cancelled: boolean = false;
		fetch(`${process.env.PRODUCTION_API_URL}chat/fetch-profile-details`, {
			method: "POST",
			cache: "no-store",
			body: JSON.stringify({
				id: receiver_business_profile_id,
				is_business_profile: true,
				// Which profile this browser acts as; the server verifies it against the session.
				viewer_is_business_profile: viewer_is_business_profile,
				...(viewer_is_business_profile ? { viewer_business_profile_id: viewer_business_profile_id } : {}),
			}),
		})
			.then((res) => res.json())
			.then((data) => {
				if (!cancelled && data && data.status === "success" && data.payload) {
					setChatUserPhoneWithCountryCode(data.payload.phone_e164 ? String(data.payload.phone_e164) : "");
				}
			})
			.catch(() => {
				// No number on a failed lookup: the button simply stays hidden.
			});
		return () => {
			cancelled = true;
		};
	}, [receiver_business_profile_id, viewer_is_business_profile, viewer_business_profile_id]);

	// The call button shows only for a business receiver, on a small screen, once the contact rule returned a number.

	const can_user_call: boolean = is_user_business_profile && smDevice && chat_user_phone_with_country_code.length > 0;

	/* Copy number (chat audit D8, 17 Oct 2026). On desktop the same number shows as the property
	   page's "View Number" button (ContactDetailCard): a click copies it, reveals it in the
	   label and shows the same success snackbar. Same gate as the call button, wider screens only. */

	const can_user_copy_number: boolean =
		is_user_business_profile && !smDevice && chat_user_phone_with_country_code.length > 0;

	const [number_revealed, setNumberRevealed] = React.useState<boolean>(false);

	const [copy_snack_open, setCopySnackOpen] = React.useState<boolean>(false);

	// A different chat starts hidden again.

	React.useEffect(() => {
		setNumberRevealed(false);
		setCopySnackOpen(false);
	}, [chat_user_phone_with_country_code]);

	const handleCopyNumber = (event: React.MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();
		setNumberRevealed(true);
		navigator.clipboard.writeText(chat_user_phone_with_country_code).catch(() => {
			// Clipboard refused: the number is still shown in the label to copy by hand.
		});
		setCopySnackOpen(true);
	};

	const chat_name: string =
		private_chat_receiver.is_business_profile === true
			? typeof private_chat_receiver.business_profile_id === "object" &&
				"title" in private_chat_receiver.business_profile_id
				? (private_chat_receiver.business_profile_id.title as string)
				: ""
			: typeof private_chat_receiver.user_id === "object" &&
				  "first_name" in private_chat_receiver.user_id &&
				  "last_name" in private_chat_receiver.user_id
				? (private_chat_receiver.user_id.first_name as string) +
					" " +
					(private_chat_receiver.user_id.last_name as string)
				: "";

	const chat_user_type: string =
		private_chat_receiver.is_business_profile === true
			? typeof private_chat_receiver.business_profile_id === "object" &&
				"business_profile_type" in private_chat_receiver.business_profile_id
				? (private_chat_receiver.business_profile_id.business_profile_type as string)
				: ""
			: localLang.client;

	const chat_user_slug: string =
		private_chat_receiver.is_business_profile === true
			? typeof private_chat_receiver.business_profile_id === "object" &&
				"slug" in private_chat_receiver.business_profile_id
				? (private_chat_receiver.business_profile_id.slug as string)
				: ""
			: typeof private_chat_receiver.user_id === "object" && "_id" in private_chat_receiver.user_id
				? (private_chat_receiver.user_id._id as string)
				: "";

	/* A deleted personal account (26 Oct 2026, lib/users/deletedUser.ts): it reads "Deleted user" and
	   its profile is never opened, so the avatar and name don't link anywhere. */
	const chat_user_is_deleted: boolean =
		private_chat_receiver.is_business_profile !== true &&
		typeof private_chat_receiver.user_id === "object" &&
		private_chat_receiver.user_id !== null &&
		(private_chat_receiver.user_id as any).is_deleted_user === true;

	/* Online / last seen (18 Oct 2026, chat/presence, lib/chat/presence.ts): read on open and every
	   30 seconds while this chat is open and the tab is visible. A broadcast with no chat yet has
	   none. A failed read keeps what is shown. */
	const [presence, setPresence] = React.useState<{ visible: boolean; online: boolean; last_seen_at: string } | null>(null);
	React.useEffect(() => {
		setPresence(null);
		if (!chat_id || chat_source_collection === "broadcast") return;
		let stopped: boolean = false;
		const read = async () => {
			if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
			try {
				const res = await fetch(process.env.PRODUCTION_API_URL + "chat/presence", {
					method: "POST",
					cache: "no-store",
					body: JSON.stringify({
						chat_id: chat_id,
						is_business_profile: profile_context?.is_business_profile === true,
						user_id: profile_context?.user_id,
						business_profile_id: profile_context?.business_profile_id,
					}),
				});
				const data = await res.json();
				if (!stopped && data?.status === "success" && data.payload) setPresence(data.payload);
			} catch (error) {
				// A failed read keeps what is shown.
			}
		};
		read();
		const timer = setInterval(read, 30 * 1000);
		return () => {
			stopped = true;
			clearInterval(timer);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chat_id, chat_source_collection]);
	const presenceLine: string =
		presence?.visible && presence.online
			? router.locale === "ar-AE"
				? "متصل الآن"
				: router.locale === "kn-IN"
					? "ಆನ್‌ಲೈನ್"
					: "online"
			: presence?.visible && presence.last_seen_at
				? formatLastSeen(presence.last_seen_at, router.locale, {
						today: (time) =>
							router.locale === "ar-AE" ? `آخر ظهور اليوم ${time}` : router.locale === "kn-IN" ? `ಇಂದು ${time} ಕ್ಕೆ ಕೊನೆಯದಾಗಿ ನೋಡಲಾಗಿದೆ` : `last seen today at ${time}`,
						yesterday: (time) =>
							router.locale === "ar-AE" ? `آخر ظهور أمس ${time}` : router.locale === "kn-IN" ? `ನಿನ್ನೆ ${time} ಕ್ಕೆ ಕೊನೆಯದಾಗಿ ನೋಡಲಾಗಿದೆ` : `last seen yesterday at ${time}`,
						date: (date) =>
							router.locale === "ar-AE" ? `آخر ظهور ${date}` : router.locale === "kn-IN" ? `${date} ರಂದು ಕೊನೆಯದಾಗಿ ನೋಡಲಾಗಿದೆ` : `last seen ${date}`,
					})
				: "";

	const chat_display_picture_url: string =
		private_chat_receiver.is_business_profile === true
			? typeof private_chat_receiver.business_profile_id === "object" &&
				"display_picture_url" in private_chat_receiver.business_profile_id
				? (private_chat_receiver.business_profile_id.display_picture_url.file_id as string)
				: ""
			: typeof private_chat_receiver.user_id === "object" && "profile_picture" in private_chat_receiver.user_id
				? (private_chat_receiver.user_id.profile_picture.file_id as string)
				: "";

	// The other side's own picture. Only an OLD one-chat-per-broadcast thread carries a ready link (the
	// listing's cover); a broadcast with no chat yet shows its sender's logo like any chat (18 Oct 2026).
	const profilePicture =
		typeof chat_display_picture_url === "string" && chat_display_picture_url.includes("/images/icons/Avatar.svg")
			? "/images/icons/Avatar.svg"
			: chat_is_broadcast && chat_source_collection !== "broadcast"
				? chat_display_picture_url
				: `/profile-picture/${chat_display_picture_url}`;

	const chat_premium_subscription_active: boolean =
		private_chat_receiver.is_business_profile === true
			? typeof private_chat_receiver.business_profile_id === "object" &&
				"subscription_information" in private_chat_receiver.business_profile_id &&
				typeof private_chat_receiver.business_profile_id.subscription_information === "object" &&
				private_chat_receiver.business_profile_id.subscription_information !== null &&
				"active" in private_chat_receiver.business_profile_id.subscription_information
				? (private_chat_receiver.business_profile_id.subscription_information as { active?: boolean }).active === true
				: false
			: false;

	// const chat_kyc_status: string =
	// 	private_chat_receiver.is_business_profile === true
	// 		? typeof private_chat_receiver.business_profile_id === "object" &&
	// 			"kyc_verified" in private_chat_receiver.business_profile_id &&
	// 			"kyc_info" in private_chat_receiver.business_profile_id &&
	// 			private_chat_receiver.business_profile_id.kyc_info &&
	// 			typeof private_chat_receiver.business_profile_id.kyc_info === "object" &&
	// 			"kyc_status" in private_chat_receiver.business_profile_id.kyc_info
	// 			? (private_chat_receiver.business_profile_id.kyc_info.kyc_status as string)
	// 			: ""
	// 		: typeof private_chat_receiver.user_id === "object" &&
	// 			  "kyc_verified" in private_chat_receiver.user_id &&
	// 			  "kyc_info" in private_chat_receiver.user_id &&
	// 			  private_chat_receiver.user_id.kyc_info &&
	// 			  typeof private_chat_receiver.user_id.kyc_info === "object" &&
	// 			  "kyc_status" in private_chat_receiver.user_id.kyc_info
	// 			? (private_chat_receiver.user_id.kyc_info.kyc_status as string)
	// 			: "";

	/* Close select dropdowns on scroll */

	React.useEffect(() => {
		const handleScroll = () => {
			setAnchorEl(null);
		};

		window.addEventListener("scroll", handleScroll);
		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, []);

	return (
		<React.Fragment>
			{/* Options Menu */}

			<Popover
				id={id}
				open={open}
				anchorEl={anchorEl}
				onClose={handleOptionsMenuClose}
				disableScrollLock={true}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				slotProps={{
					paper: {
						sx: {
							height: "auto",
							borderRadius: "16px",
							padding: "0.5rem",
							gap: "0rem",
							backgroundColor: theme.palette.background.paper,
							backgroundImage: "none",
							scrollbarWidth: "none",
							direction: router.locale === "ar-AE" ? "rtl" : "ltr",
						},
					},
				}}
			>
				{/* Search inside the conversation, first in the menu as in the app (gap A9). */}
				{on_open_search ? (
					<CustomMenuItem
						onClick={() => {
							handleOptionsMenuClose();
							on_open_search();
						}}
					>
						{localLang.search}
					</CustomMenuItem>
				) : null}
				{/* Media, links and docs, and what else was shared (gap A10); a broadcast with no chat yet has none. */}
				{chat_source_collection !== "broadcast" ? (
					<CustomMenuItem
						onClick={() => {
							handleOptionsMenuClose();
							setOpenSharedDialog(true);
						}}
					>
						{localLang.media_links_docs}
					</CustomMenuItem>
				) : null}
				{/* A broadcast with no chat yet has no chat to mute or delete until the viewer acts. */}
				{chat_source_collection !== "broadcast" ? (
					<CustomMenuItem onClick={handleOpenMuteDialog}>{chat_is_muted ? localLang.unmute : localLang.mute}</CustomMenuItem>
				) : null}
				{/* Report the profile on the other side (18 Oct 2026), with "Also block" in its dialog. */}
				<CustomMenuItem
					onClick={() => {
						handleOptionsMenuClose();
						setOpenReportDialog(true);
					}}
					sx={{
						color: theme.palette.error.main,
					}}
				>
					{localLang.report}
				</CustomMenuItem>
				<CustomMenuItem
					onClick={handleBlockMenuItem}
					sx={{
						color: blockedByMe ? theme.palette.text.primary : theme.palette.error.main,
					}}
				>
					{blockedByMe ? localLang.unblock : localLang.block}
				</CustomMenuItem>
				{chat_source_collection !== "broadcast" ? (
					<CustomMenuItem
						onClick={handleOpenDeleteDialog}
						sx={{
							color: theme.palette.error.main,
						}}
					>
						{localLang.delete_chat}
					</CustomMenuItem>
				) : null}
			</Popover>

			{chat_source_collection !== "broadcast" ? (
				<ChatSharedDialog
					open={openSharedDialog}
					onClose={() => setOpenSharedDialog(false)}
					chat_id={String(chat_id)}
					profile_context={profile_context}
				/>
			) : null}

			{/* Block: reasons first, then Block (the app's sheet, as a dialog) */}

			<Dialog
				open={openBlockDialog}
				onClose={handleCloseBlockDialog}
				aria-labelledby="block-dialog-title"
				PaperProps={{
					sx: {
						borderRadius: "1rem",
						padding: "0.5rem",
						maxWidth: "26rem",
					},
				}}
			>
				<DialogTitle
					id="block-dialog-title"
					sx={{
						fontSize: "1rem",
						fontWeight: 600,
						color: theme.palette.text.primary,
					}}
				>
					{localLang.block_title}
				</DialogTitle>

				<DialogContent>
					<DialogContentText
						sx={{
							fontSize: "0.875rem",
							color: theme.palette.text.secondary,
							marginBottom: "0.5rem",
						}}
					>
						{localLang.block_body}
					</DialogContentText>

					{BLOCK_REASONS.map((reason: string) => (
						<div
							key={reason}
							onClick={() => {
								if (blocking) return;
								setBlockReasons((current) =>
									current.includes(reason) ? current.filter((r) => r !== reason) : [...current, reason],
								);
							}}
							style={{ display: "flex", alignItems: "center", gap: "0.25rem", cursor: "pointer" }}
						>
							<Checkbox
								checked={blockReasons.includes(reason)}
								disabled={blocking}
								size="small"
								tabIndex={-1}
							/>
							<Typography variant="body2">{reason}</Typography>
						</div>
					))}

					<div
						onClick={() => (blocking ? null : setBlockAlsoReport((value) => !value))}
						style={{ display: "flex", alignItems: "center", gap: "0.25rem", cursor: "pointer", marginTop: "0.5rem" }}
					>
						<Checkbox checked={blockAlsoReport} disabled={blocking} size="small" tabIndex={-1} />
						<Typography variant="body2" sx={{ fontWeight: 600 }}>
							{localLang.also_report}
						</Typography>
					</div>

					{blockError ? (
						<Typography
							variant="body2"
							role="alert"
							sx={{
								fontSize: "0.8125rem",
								color: theme.palette.error.main,
								marginTop: "0.75rem",
							}}
						>
							{localLang.block_error}
						</Typography>
					) : null}
				</DialogContent>

				<DialogActions sx={{ padding: "1rem" }}>
					<BeegruButton
						variant="outlined"
						disabled={blocking}
						onClick={handleCloseBlockDialog}
						sx={{
							textTransform: "none",
							color: theme.palette.text.secondary,
						}}
					>
						{localLang.no_cancel}
					</BeegruButton>

					<BeegruButton
						onClick={handleBlock}
						variant="contained"
						flavor="error"
						disabled={blockReasons.length === 0}
						loading={blocking}
						sx={{
							textTransform: "none",
							borderRadius: "0.5rem",
						}}
					>
						{localLang.block}
					</BeegruButton>
				</DialogActions>
			</Dialog>

			{/* Report the profile (18 Oct 2026); "Also block" while this side hasn't blocked them. */}
			<ChatReportDialog
				open={openReportDialog}
				onClose={() => setOpenReportDialog(false)}
				dispatch={dispatch}
				locale={router.locale}
				profile_context={profile_context}
				chat_id={chat_source_collection === "broadcast" ? "" : String(chat_id || "")}
				message_id=""
				broadcast_id={chat_source_collection === "broadcast" ? String(chat_broadcast_id || "") : undefined}
				block_target={
					blockedByMe
						? null
						: {
								is_business_profile: private_chat_receiver?.is_business_profile === true,
								id:
									private_chat_receiver?.is_business_profile === true
										? idOf(private_chat_receiver?.business_profile_id)
										: idOf(private_chat_receiver?.user_id),
							}
				}
				onDone={(blocked) => {
					setOpenReportDialog(false);
					setReportNotice(blocked ? localLang.report_and_block_sent : localLang.report_sent);
				}}
			/>
			<Snackbar open={reportNotice !== ""} autoHideDuration={3000} onClose={() => setReportNotice("")} message={reportNotice} />

			{/* Unblock: this side's own block, lifted after a confirm */}

			<Dialog
				open={openUnblockDialog}
				onClose={() => (unblocking ? undefined : setOpenUnblockDialog(false))}
				aria-labelledby="unblock-dialog-title"
				PaperProps={{ sx: { borderRadius: "1rem", padding: "0.5rem", maxWidth: "26rem" } }}
			>
				<DialogTitle id="unblock-dialog-title" sx={{ fontSize: "1rem", fontWeight: 600, color: theme.palette.text.primary }}>
					{localLang.unblock_title}
				</DialogTitle>
				<DialogContent>
					<DialogContentText sx={{ fontSize: "0.875rem", color: theme.palette.text.secondary }}>
						{localLang.unblock_body}
					</DialogContentText>
					{unblockError ? (
						<Typography variant="body2" role="alert" sx={{ fontSize: "0.8125rem", color: theme.palette.error.main, marginTop: "0.75rem" }}>
							{localLang.unblock_error}
						</Typography>
					) : null}
				</DialogContent>
				<DialogActions sx={{ padding: "1rem" }}>
					<BeegruButton
						variant="outlined"
						disabled={unblocking}
						onClick={() => setOpenUnblockDialog(false)}
						sx={{ textTransform: "none", color: theme.palette.text.secondary }}
					>
						{localLang.no_cancel}
					</BeegruButton>
					<BeegruButton
						onClick={handleUnblock}
						variant="contained"
						loading={unblocking}
						sx={{ textTransform: "none", borderRadius: "0.5rem" }}
					>
						{localLang.unblock}
					</BeegruButton>
				</DialogActions>
			</Dialog>

			{/* Mute / Unmute, for the signed-in person only */}

			<Dialog
				open={openMuteDialog}
				onClose={handleCloseMuteDialog}
				aria-labelledby="mute-dialog-title"
				PaperProps={{ sx: { borderRadius: "1rem", padding: "0.5rem", maxWidth: "26rem" } }}
			>
				<DialogTitle id="mute-dialog-title" sx={{ fontSize: "1rem", fontWeight: 600, color: theme.palette.text.primary }}>
					{muteDialogMutes ? localLang.mute_title : localLang.unmute_title}
				</DialogTitle>
				<DialogContent>
					<DialogContentText sx={{ fontSize: "0.875rem", color: theme.palette.text.secondary }}>
						{muteDialogMutes ? localLang.mute_body : localLang.unmute_body}
					</DialogContentText>
					{muteError ? (
						<Typography variant="body2" role="alert" sx={{ fontSize: "0.8125rem", color: theme.palette.error.main, marginTop: "0.75rem" }}>
							{localLang.mute_error}
						</Typography>
					) : null}
				</DialogContent>
				<DialogActions sx={{ padding: "1rem" }}>
					<BeegruButton
						variant="outlined"
						disabled={savingMute}
						onClick={handleCloseMuteDialog}
						sx={{ textTransform: "none", color: theme.palette.text.secondary }}
					>
						{localLang.no_cancel}
					</BeegruButton>
					<BeegruButton
						onClick={handleMute}
						variant="contained"
						loading={savingMute}
						sx={{ textTransform: "none", borderRadius: "0.5rem" }}
					>
						{muteDialogMutes ? localLang.mute : localLang.unmute}
					</BeegruButton>
				</DialogActions>
			</Dialog>

			{/* Delete Confirmation Modal */}

			<Dialog
				open={openDeleteDialog}
				onClose={handleCloseDeleteDialog}
				aria-labelledby="delete-dialog-title"
				aria-describedby="delete-dialog-description"
				PaperProps={{
					sx: {
						borderRadius: "1rem",
						padding: "0.5rem",
					},
				}}
			>
				<DialogTitle
					id="delete-dialog-title"
					sx={{
						fontSize: "1rem",
						fontWeight: 600,
						color: theme.palette.text.primary,
					}}
				>
					{localLang.delete_chat}
				</DialogTitle>

				<DialogContent>
					<DialogContentText
						id="delete-dialog-description"
						sx={{
							fontSize: "0.875rem",
							color: theme.palette.text.secondary,
						}}
					>
						{localLang.delete_chat_confirmation}
					</DialogContentText>

					{/* Failure line (chat audit I11, 17 Oct 2026) */}

					{deleteChatError ? (
						<Typography
							variant="body2"
							role="alert"
							sx={{
								fontSize: "0.8125rem",
								color: theme.palette.error.main,
								marginTop: "0.75rem",
							}}
						>
							{localLang.delete_chat_error}
						</Typography>
					) : null}
				</DialogContent>

				<DialogActions sx={{ padding: "1rem" }}>
					<BeegruButton
						onClick={handleDeleteChat}
						variant="text"
						flavor="error"
						loading={deletingChat}
						sx={{
							textTransform: "none",
							borderRadius: "0.5rem",
						}}
					>
						{localLang.yes_delete}
					</BeegruButton>

					<BeegruButton
						variant="outlined"
						disabled={deletingChat}
						onClick={handleCloseDeleteDialog}
						sx={{
							textTransform: "none",
							color: theme.palette.text.secondary,
						}}
					>
						{localLang.no_cancel}
					</BeegruButton>
				</DialogActions>
			</Dialog>

			{/* Copy number feedback, the property page's snackbar (chat audit D8, 17 Oct 2026) */}

			<Snackbar
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				open={copy_snack_open}
				autoHideDuration={3000}
				onClose={() => setCopySnackOpen(false)}
			>
				<Alert
					onClose={() => setCopySnackOpen(false)}
					severity="success"
					variant="filled"
					elevation={6}
					sx={{ width: "100%", borderRadius: "0.75rem" }}
				>
					{localLang.mention_snack}
				</Alert>
			</Snackbar>

			{/* Main Content */}

			<RootDiv>
				{/* The condition below hides the back arrow button when the ChatComponent is in fullscreen mode */}

				<RowContainer sx={{ gap: "0.25rem" }}>
					{/* Back Arrow */}

					<Tooltip title={localLang.back_to_chats}>
						<IconButton
							onClick={() => {
								dispatch(setChatScreenThunk("chat_list"));
							}}
						>
							<ArrowBackOutlined
								sx={{
									cursor: "pointer",
									color: theme.palette.text.disabled,
									fontSize: "1.5rem",
								}}
							/>
						</IconButton>
					</Tooltip>

					{/* Avatar Section */}

					<Tooltip title={chat_name}>
						<RowContainer
							onClick={() => {
								if (chat_user_is_deleted) return;
								// A person's profile is /users/<id> (chat audit I11, 17 Oct 2026): the translated
								// "Client" label made /Clients/<id>, which 404s. Business links are unchanged.
								const profile_path: string =
									private_chat_receiver.is_business_profile === true
										? `${chat_user_type}s/${chat_user_slug}`
										: `users/${chat_user_slug}`;
								router.push(
									`${process.env.PRODUCTION_URL || ""}loading.html?callbackUrl=${encodeURIComponent(
										`${process.env.PRODUCTION_URL || ""}${router.locale}/${profile_path}`,
									)}&locale=${router.locale || "en-IN"}`,
								);
							}}
							sx={{
								justifyContent: "flex-start",
								alignItems: "flex-start",
								gap: "0.75rem",
								cursor: chat_user_is_deleted ? "default" : "pointer",
							}}
						>
							{/* Profile Picture */}

							<CustomImg
								src={profilePicture as string}
								alt={chat_name}
							/>

							<ColumnContainer
								sx={{
									gap: "0rem",
								}}
							>
								<RowContainer
									sx={{
										justifyContent: "flex-start",
										gap: "0.5rem",
									}}
								>
									{/* Title */}

									<Heading variant="body2">
										{chat_name}
										{/* {chat_name.length > 14 ? chat_name.substring(0, 14) + "..." : chat_name} */}
									</Heading>

									{/* Premium Badge */}

									{chat_premium_subscription_active ? (
										<Tooltip
											open={openPremiumTooltip}
											onClick={() => setOpenPremiumTooltip(true)}
											onClose={() => setOpenPremiumTooltip(false)}
											onOpen={() => setOpenPremiumTooltip(true)}
											title={localLang.premium_user}
										>
											<SvgIcon
												component={PremiumBadge}
												inheritViewBox={false}
												viewBox="0 0 24 24"
												width={24}
												height={24}
												sx={{
													width: "1rem",
													height: "1rem",
												}}
											/>
										</Tooltip>
									) : null}
								</RowContainer>

								{/* Profile Type */}

								{/* "typing…", then "online" / "last seen …" (18 Oct 2026, hidden across a block),
								    then the profile type. */}
								<SubHeading
									variant="body2"
									sx={is_typing ? { color: theme.palette.primary.main } : undefined}
								>
									{is_typing
										? router.locale === "ar-AE"
											? "يكتب…"
											: router.locale === "kn-IN"
												? "ಟೈಪ್ ಮಾಡುತ್ತಿದ್ದಾರೆ…"
												: "typing…"
										: presenceLine
											? presenceLine
											: private_chat_receiver.is_business_profile
												? BusinessProfileTypeString(chat_user_type, router)
												: chat_user_type}
								</SubHeading>
							</ColumnContainer>
						</RowContainer>
					</Tooltip>
				</RowContainer>

				<RowContainer
					sx={{
						justifyContent: "flex-start",
						gap: "0.25rem",
					}}
				>
					{/* Copy number, desktop (chat audit D8, 17 Oct 2026) */}
					{can_user_copy_number && (
						<BeegruButton
							flavor="success"
							variant="contained"
							size="small"
							startIcon={<LocalPhoneOutlined />}
							onClick={handleCopyNumber}
							sx={{
								textTransform: "none",
								whiteSpace: "nowrap",
							}}
						>
							{number_revealed ? chat_user_phone_with_country_code : localLang.view_number}
						</BeegruButton>
					)}

					{/* Call now */}
					{can_user_call && (
						<Tooltip title={localLang.call}>
							<IconButton
								href={`tel:${chat_user_phone_with_country_code}`}
								target="_blank"
							>
								<CallIcon
									sx={{
										color: theme.palette.success.main,
										fontSize: "1.5rem",
									}}
								/>
							</IconButton>
						</Tooltip>
					)}

					{/* WhatsApp, beside Call (3 Nov 2026, the app's chat details): the same number, the same rule for showing it. */}
					{(can_user_call || can_user_copy_number) && chat_user_phone_with_country_code.replace(/\D/g, "").length >= 8 ? (
						<Tooltip title="WhatsApp">
							<IconButton
								href={`https://wa.me/${chat_user_phone_with_country_code.replace(/\D/g, "")}`}
								target="_blank"
								rel="noopener noreferrer"
								aria-label="WhatsApp"
							>
								<WhatsApp sx={{ color: "#25D366", fontSize: "1.5rem" }} />
							</IconButton>
						</Tooltip>
					) : null}

					{/* Options Icon */}

					<Tooltip title={localLang.options}>
						<IconButton onClick={handleOptionsMenuClick}>
							<MoreVertOutlined
								sx={{
									color: theme.palette.text.secondary,
									fontSize: "1.5rem",
								}}
							/>
						</IconButton>
					</Tooltip>

					{/* Close Icon */}

					<Tooltip title={localLang.close}>
						<IconButton
							aria-label="close"
							onClick={() => dispatch(setOpenChatModalThunk(false))}
						>
							<ExpandMoreOutlined
								sx={{
									color: theme.palette.text.disabled,
									fontSize: "1.75rem",
								}}
							/>
						</IconButton>
					</Tooltip>
				</RowContainer>
			</RootDiv>
		</React.Fragment>
	);
};

export default ChatHeader;
